import * as Immutable from 'immutable';
import { handleActions } from 'redux-actions';
import { v1 } from 'uuid';

import { APP_EMAIL } from '../../config';
import { SUPPORT_URL_PLACEHOLDER } from '../../shared/constants';

import {
  BaseFlashMessageOptionsStore,
  BaseFlashMessageStoreElement,
  FlashMessagesStoreElement,
  FlashMessageActionType,
} from './FlashMessageReducer.types';

const removeAll = (state: FlashMessagesStoreElement, path: string[], itemsToRemove: string[]) => {
  itemsToRemove.forEach((item: string) => {
    // @ts-ignore
    state = state.updateIn(path, v => v.remove(item));
  });

  return state;
};

export const initialOptions: BaseFlashMessageOptionsStore = {
  type: 'error',
  links: null,
  parseHtml: false,
  context: 'generic',
  location: 'top',
  timerOut: null,
  errorLog: null,
  intl: true,
  handleOpenGuidelines: null,
  notBlink: false,
  excludedLinks: [],
};

// @ts-ignore
const initialState: FlashMessagesStoreElement = Immutable.fromJS({
  messages: { top: {} },
  context: Immutable.fromJS(['generic']),
});

export const setOptions = (action: FlashMessageActionType) => {
  let options: BaseFlashMessageOptionsStore = initialOptions;
  if (action.payload.options) {
    const location = !action.payload.options.location ? 'top' : action.payload.options.location;
    const context = action.payload.options.context === undefined ? 'generic' : action.payload.options.context;

    const payloadOptions = { ...action.payload.options, ...{ location, context } };
    options = { ...options, ...payloadOptions };
  }

  return options;
};

export const replacePlaceholderURLs = (options: BaseFlashMessageOptionsStore, isLoggedIn: boolean): void => {
  if (!options.links || options.links.length === 0) {
    return;
  }

  for (const link of options.links) {
    if (link.urls && options.links.length > 0) {
      link.urls = link.urls.map(url => {
        if (url === SUPPORT_URL_PLACEHOLDER) {
          return isLoggedIn ? '/contact-us' : `mailto:${APP_EMAIL}`;
        } else {
          return url;
        }
      });
    }
  }
};

export const checkIfMessageAlreadyExists = (messageId: Immutable.List<string>, messagesFromContext: any) => {
  let index = -1;
  messagesFromContext.find((contextMessages: any, key: number) => {
    const flashMessages = contextMessages.get('message').map((message: any) => {
      if (typeof message === 'string') {
        return message;
      } else {
        return message.get('msg');
      }
    });
    if (Immutable.is(messageId, flashMessages)) {
      index = key;

      return true;
    } else {
      return false;
    }
  });

  return index;
};

const FlashMessageReducer = handleActions(
  {
    ADD_FLASH_MESSAGES: (state: FlashMessagesStoreElement, action: FlashMessageActionType) => {
      const isLoggedIn: boolean = action.payload.isLoggedIn ? action.payload.isLoggedIn : false;

      const options = setOptions(action);
      replacePlaceholderURLs(options, isLoggedIn);

      const messages = state.getIn(['messages', options.location, options.context]);
      const newMessagesObject: any = action.payload.message;
      const payloadMessages =
        action.payload.message &&
        Immutable.fromJS(
          newMessagesObject.map((messagePayload: any) => {
            if (typeof messagePayload === 'string') {
              return messagePayload;
            } else {
              return messagePayload.msg;
            }
          })
        );

      // @ts-ignore
      if (payloadMessages && messages && messages.size > 0) {
        // @ts-ignore
        const index = checkIfMessageAlreadyExists(payloadMessages, state.getIn(['messages', options.location, options.context]));
        if (options.context && index >= 0) {
          return state.setIn(['messages', options.location, options.context, index, 'options', 'blink'], true);
        }
      }
      // @ts-ignore
      const message: BaseFlashMessageStoreElement = Immutable.fromJS({
        message: action.payload.message,
        options,
        id: v1(),
      });
      let newMessages = Immutable.List();
      // @ts-ignore
      newMessages = messages ? messages.push(message) : newMessages.push(message);

      return state.setIn(['messages', options.location, options.context], newMessages);
    },
    REMOVE_FLASH_MESSAGE: (state: FlashMessagesStoreElement, action: FlashMessageActionType) => {
      if (action.payload.path) {
        const path = [...['messages'], ...action.payload.path];

        if (action.payload.id !== undefined) {
          return state.updateIn(path, (list: any) => list.filter((val: any) => val.get('id') !== action.payload.id));
        } else {
          return state.updateIn(path, () => Immutable.List());
        }
      } else {
        return state;
      }
    },

    REMOVE_BLINK_FLASH_MESSAGE: (state: FlashMessagesStoreElement, action: FlashMessageActionType) => {
      if (action.payload.path) {
        const path = [...['messages'], ...action.payload.path];

        if (action.payload.id !== undefined) {
          return state.updateIn(path, (list: any) =>
            list.map((val: any) => {
              if (val.get('id') !== action.payload.id) {
                return val;
              } else {
                return val.setIn(['options', 'blink'], false);
              }
            })
          );
        } else {
          return state.updateIn(path, () => Immutable.List());
        }
      } else {
        return state;
      }
    },

    REMOVE_ALL_FLASH_MESSAGES: (state: FlashMessagesStoreElement) => {
      let newState = state;

      // Messages with some contexts should not be remove;
      const contextExceptions: any = ['edit-passord'];

      state.get('messages').map((message: any, location: string) => {
        message.map((_: any, context: string) => {
          if (contextExceptions.includes(context) === false && location !== 'editor-code') {
            newState = newState.setIn(['messages', location, context], Immutable.fromJS([]));
          }
        });
      });

      return newState;
    },

    REMOVE_FLASH_MESSAGES_CONTEXTS: (state: FlashMessagesStoreElement, action: FlashMessageActionType) => {
      if (action.payload.path && action.payload.contexts) {
        const path = [...['messages'], ...action.payload.path];
        let newState = state;
        action.payload.contexts.forEach((context: string) => {
          const newPath = [...path, ...[context]];
          newState = newState.updateIn(newPath, () => Immutable.List());
        });

        return newState;
      } else {
        return state;
      }
    },

    REMOVE_FLASH_MESSAGES_CONTEXTS_ERRORS: (state: FlashMessagesStoreElement, action: FlashMessageActionType) => {
      if (action.payload.path && action.payload.contexts) {
        const path = [...['messages'], ...action.payload.path];
        let newState = state;

        action.payload.contexts.forEach((context: string) => {
          const newPath = [...path, ...[context]];
          const newList = state.getIn(newPath);
          newState = newState.updateIn(newPath, () =>
            newList
              ? // @ts-ignore
                newList.filter((el: any) => el.getIn(['options', 'type']) !== 'error')
              : Immutable.List()
          );
        });

        return newState;
      } else {
        return state;
      }
    },

    REMOVE_FLASH_CONTEXTS: (state: FlashMessagesStoreElement, action: FlashMessageActionType) => {
      if (action.payload.path && action.payload.contexts) {
        const path = [...['messages'], ...action.payload.path];
        let contexts = state.getIn(path);
        if (contexts !== undefined) {
          action.payload.contexts.forEach((ctx: any) => {
            // @ts-ignore
            contexts = contexts.delete(ctx);
          });

          return state.setIn(path, contexts);
        } else {
          return state;
        }
      } else {
        return state;
      }
    },

    ONLY_GENERIC_FLASH_MESSAGES: (state: FlashMessagesStoreElement) =>
      state.updateIn(['messages', 'top'], (contexts: any) =>
        contexts.get('generic') ? Immutable.fromJS({ generic: contexts.get('generic') }) : Immutable.fromJS({})
      ),

    REMOVE_FLASH_LOCATIONS: (state: FlashMessagesStoreElement, action: FlashMessageActionType) => {
      if (action.payload.locations) {
        return removeAll(state, ['messages'], action.payload.locations);
      } else {
        return state;
      }
    },

    REMOVE_ACTIVE_CONTEXTS: (state: FlashMessagesStoreElement, action: FlashMessageActionType) => {
      if (action.payload.contexts) {
        const contextsToRemove: any = action.payload.contexts;

        return state.update('context', (list: Immutable.List<string>) =>
          list.filter((ctx: string) => contextsToRemove.includes(ctx) === false)
        );
      } else {
        return state;
      }
    },

    CHANGE_ACTIVE_CONTEXTS: (state: FlashMessagesStoreElement, action: FlashMessageActionType) => {
      if (action.payload.contexts !== undefined) {
        const contexts: string[] = action.payload.contexts;

        return state.update('context', () => Immutable.List(contexts));
      } else {
        return state;
      }
    },

    ADD_ACTIVE_CONTEXTS: (state: FlashMessagesStoreElement, action: FlashMessageActionType) => {
      if (action.payload.contexts) {
        const newContexts = action.payload.contexts.filter((ctx: string) => state.get('context').includes(ctx) === false);
        if (newContexts.length !== 0) {
          return state.update('context', (list: Immutable.List<string>) => list.concat(newContexts));
        } else {
          return state;
        }
      } else {
        return state;
      }
    },

    REPLACE_ACTIVE_CONTEXTS: (state: FlashMessagesStoreElement, action: FlashMessageActionType) => {
      if (action.payload.searchContext && action.payload.newcontexts) {
        const contextsToRemove: any = action.payload.searchContext;
        if (contextsToRemove.filter((ctx: string) => state.get('context').includes(ctx) === true).length !== 0) {
          return state
            .update('context', (list: Immutable.List<string>) => list.filter((ctx: any) => contextsToRemove.includes(ctx) === false))
            .update('context', (list: Immutable.List<string>) => {
              if (action.payload.newcontexts) {
                return list.concat(action.payload.newcontexts);
              } else {
                return list;
              }
            });
        } else {
          return state;
        }
      } else {
        return state;
      }
    },
    CLEAR_ALL_FLASH_CONTEXTS: (state: FlashMessagesStoreElement) => state.set('context', Immutable.List(['generic'])),
    INIT_FLASH_MESSAGES: () => initialState,
  },
  initialState
);

export default FlashMessageReducer;
